/*
 * <cart-gift-bag> — manual "Añade una bolsa de regalo" add-on row for the cart drawer.
 *
 * Mirrors the theme's existing <cart-gift-manager> (assets/base.js → CartGiftManager):
 *   - POSTs to /cart/add.js / /cart/change.js with ?sections=<cart-drawer> so Shopify
 *     returns the re-rendered drawer markup,
 *   - shows the shared [gift-loader-overlay] spinner while the request is in flight,
 *   - then dispatches the theme cart-update event ('action:cart:updateCart') with
 *     { sections } so the drawer re-renders itself.
 *
 * Difference from CartGiftManager: this one is USER-DRIVEN via a checkbox (not a
 * threshold), so it never auto-adds/removes. It only reacts to the checkbox toggle,
 * and reflects the cart's current state (checked when the gift product is in the cart).
 *
 * The row is ALWAYS rendered (the Liquid snippet never hides it) — it must never
 * disappear. The checkbox just mirrors whether the configured product is in the cart:
 * ticking adds it, unticking removes it. Everything is handled from this one control.
 *
 * Dependency-free: uses window.Shopify.routes + native fetch + CustomEvent so it works
 * even if base.js internals are unavailable. The event name matches
 * _EVENT_HELPER.triggerCartChange in base.js.
 */
(function () {
  'use strict';

  if (window.customElements && customElements.get('cart-gift-bag')) return;

  // Same string as base.js _EVENT_HELPER.triggerCartChange — the drawer listens for it.
  var CART_UPDATE_EVENT = 'action:cart:updateCart';
  var SECTION_ID = 'cart-drawer';

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  class CartGiftBag extends HTMLElement {
    constructor() {
      super();
      this.giftVariantId = Number(this.getAttribute('gift-variant-id'));
      this._busy = false;
      this._onToggle = this._onToggle.bind(this);
      this._onCartChange = this._onCartChange.bind(this);
    }

    connectedCallback() {
      if (!this.giftVariantId) return;

      this.checkbox = this.querySelector('input[type="checkbox"]');
      this.overlay = document.querySelector('[gift-loader-overlay]');

      if (this.checkbox) {
        this.checkbox.addEventListener('change', this._onToggle);
      }

      // Reflect current cart state, and keep it in sync with other cart mutations.
      document.addEventListener(CART_UPDATE_EVENT, this._onCartChange);
      this._syncFromCart();
    }

    disconnectedCallback() {
      if (this.checkbox) this.checkbox.removeEventListener('change', this._onToggle);
      document.removeEventListener(CART_UPDATE_EVENT, this._onCartChange);
    }

    _onCartChange() {
      // Don't fight an in-flight request we started ourselves.
      if (this._busy) return;
      this._syncFromCart();
    }

    _showOverlay() {
      if (this.overlay) this.overlay.classList.add('gift-loader-overlay--active');
    }

    _hideOverlay() {
      if (this.overlay) this.overlay.classList.remove('gift-loader-overlay--active');
    }

    _setChecked(checked) {
      if (this.checkbox) this.checkbox.checked = checked;
      this.toggleAttribute('checked', checked);
    }

    _setDisabled(disabled) {
      if (this.checkbox) this.checkbox.disabled = disabled;
    }

    /* Read the cart and tick/untick the box based on whether the gift product is present. */
    _syncFromCart() {
      var self = this;
      fetch(routeRoot() + 'cart.js', {
        headers: { Accept: 'application/json' }
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          self._setChecked(self._findGiftLine(cart) !== null);
        })
        .catch(function () { /* leave as-is on failure */ });
    }

    _findGiftLine(cart) {
      if (!cart || !cart.items) return null;
      for (var i = 0; i < cart.items.length; i++) {
        if (cart.items[i].variant_id === this.giftVariantId) return cart.items[i];
      }
      return null;
    }

    _onToggle() {
      if (this._busy) return;
      var wantsGift = this.checkbox ? this.checkbox.checked : false;

      this._busy = true;
      this._setDisabled(true);
      this._showOverlay();

      if (wantsGift) {
        this._addGift();
      } else {
        // Need the line key to remove — look it up first.
        var self = this;
        fetch(routeRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var line = self._findGiftLine(cart);
            if (line) {
              self._removeGift(line.key);
            } else {
              self._finish(false); // already absent, nothing to do
            }
          })
          .catch(function () { self._finish(true); });
      }
    }

    _addGift() {
      var self = this;
      var url = routeRoot() + 'cart/add.js?sections=' + SECTION_ID;
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: this.giftVariantId, quantity: 1 }] })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) { self._afterMutation(data && data.sections); })
        .catch(function () { self._finish(true); });
    }

    _removeGift(lineKey) {
      var self = this;
      var url = routeRoot() + 'cart/change.js?sections=' + SECTION_ID;
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) { self._afterMutation(data && data.sections); })
        .catch(function () { self._finish(true); });
    }

    /* Tell the drawer to re-render with the freshly returned section HTML. */
    _afterMutation(sections) {
      document.dispatchEvent(
        new CustomEvent(CART_UPDATE_EVENT, {
          bubbles: true,
          detail: { sections: sections }
        })
      );
      this._finish(false);
    }

    /*
     * revertCheckbox=true → the request failed, so put the box back to the real cart state.
     * The drawer re-render will replace this element anyway on success, but we still clear
     * busy/overlay so a non-re-rendering path (e.g. failure) leaves the UI usable.
     */
    _finish(revertCheckbox) {
      this._busy = false;
      this._setDisabled(false);
      this._hideOverlay();
      if (revertCheckbox) this._syncFromCart();
    }
  }

  customElements.define('cart-gift-bag', CartGiftBag);
})();
